import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export const RELOAD_FLAG = 'lazy-chunk-reload';
/**
 * WEB-1336 (десятка 4, п.7): sessionStorage-флаг (RELOAD_FLAG) — единственный
 * носитель признака «уже перезагружали». Когда storage заблокирован (Safari
 * «Блокировать все cookie», некоторые встроенные браузеры), safeStorageGet
 * ВСЕГДА возвращает null — гейт никогда не срабатывает, и настоящий сбой CDN
 * (не однократный сбой, а реальный аутаж) уходит в вечный reload вместо
 * мягкого отказа (throw → React error boundary / blank fallback вместо
 * reload-петли). Второй, storage-независимый носитель: query-параметр в
 * самом URL — он переживает отсутствие sessionStorage, потому что приходит
 * со следующей загрузки страницы, а не читается из хранилища на этой же.
 */
export const RELOAD_QUERY_PARAM = 'chunk_retry';

type ModuleDefault<T> = { default: T };
type Importer<T> = () => Promise<ModuleDefault<T>>;

/**
 * WEB-1336 (П3): Safari with "Block all cookies" (and some in-app browsers)
 * makes EVERY sessionStorage access throw a SecurityError — even reading
 * `window.sessionStorage` itself. Before this guard, a throw from removeItem
 * AFTER a successful chunk load (or the getItem inside the catch) escaped
 * retryImport entirely, and React.lazy without an error boundary rendered a
 * blank white page — on the money-bearing public routes of all places
 * (router/index.tsx wires PublicQuizPage/PublicPaywallPage through here).
 * Same try/catch discipline as useDismissableInformer.ts / billingSync.ts.
 * With no storage we simply lose the "already reloaded once" memory: a
 * genuine outage then reloads on every mount attempt, which is still
 * strictly better than a permanent blank screen.
 */
function safeStorageGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked — skip remembering; see doc above.
  }
}

function safeStorageRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Storage blocked — nothing to clear.
  }
}

/**
 * ЗАЩЁЛКА «УЖЕ ПЕРЕЗАГРУЖАЛИСЬ» — ПО ФАЙЛУ, А НЕ НА ВСЮ СЕССИЮ
 * (свежий взгляд, Н1).
 *
 * ЧТО БЫЛО. Через эту обёртку раньше ходила одна страница пейволла, а после
 * WEB-2054 — ещё и куски рендерера блоков. Защёлка же
 * осталась ОДНА на визит: первый же кусок, который не нашёлся, тратил её на
 * всех остальных. Сценарий с боя: кусок A не приехал → перезагрузка с
 * `?chunk_retry=1` → A приехал, флаг хранилища снят, а ПАРАМЕТР В АДРЕСЕ так и
 * остался (его не снимал никто) → кусок B (другой файл) не приехал → `throw`
 * без перезагрузки → блок пуст до конца визита, хотя лечение было ровно одно —
 * сходить за свежим `index.html`.
 *
 * КАК СТАЛО. Оба носителя (флаг хранилища и параметр адреса) несут СПИСОК
 * ключей файлов, за которые перезагрузка уже была. Успех вычёркивает свой
 * ключ из обоих — в том числе из адреса, тем же `history.replaceState`, не
 * трогая остальной query и hash. Отказ смотрит только на СВОЙ ключ: свой в
 * списке — бросаем (круг «файл → перезагрузка → тот же файл» невозможен),
 * чужой — перезагружаемся, но не больше `MAX_RELOADS_PER_VISIT` раз за визит
 * (аутаж CDN не должен превращаться в карусель перезагрузок).
 *
 * СТАРОЕ ЗНАЧЕНИЕ (`true` во флаге, `chunk_retry=1` в адресе) читается как
 * «перезагрузка уже была ЗА ВСЁ»: посетитель, поймавший выкат в середине
 * визита, ведёт себя как раньше, а не получает лишнюю перезагрузку.
 *
 * ЧТО ЧЕРЕЗ ОБЁРТКУ НЕ ХОДИТ (К4, исправление неверного утверждения шапки):
 * пакеты языков воронки. `loadPublicQuizStrings` в
 * `utils/publicQuizStrings.ts` зовёт голый `import()` и лечится иначе —
 * забывает отказ, чтобы следующий заказ попробовал снова, а до тех пор
 * надписи английские, а не пустые. Перезагружать ради них страницу незачем,
 * и это сделано осознанно.
 */
const MAX_RELOADS_PER_VISIT = 3;
const LEGACY_MARK = new Set(['1', 'true']);

/**
 * КЛЮЧ ЗАЩЁЛКИ ЗАДАЁТСЯ ЯВНО В МЕСТЕ ВЫЗОВА (свежий взгляд пачки 36, проход 3).
 *
 * ПОЧЕМУ НЕ ИЗ ТЕКСТА ИМПОРТЁРА. Две попытки подряд считали ключ из
 * `String(importer)`: там же стоит адрес модуля. Но этот текст пишет СБОРЩИК, и
 * его форма — не наш контракт. Первая попытка не учла хеш в имени файла
 * (`PublicPaywall-BpFi.js` после каждого выката другой → успех не снимал
 * защёлку). Вторая сняла хвост `-<хеш>` у адресов в `assets/` — а настоящая
 * сборка (`frontend/dist`) пишет импорты соседей БЕЗ `assets/`
 * (`import("./Login-nT0Q8-XD.js")`, 46 из 46), и хеш Rollup сам содержит дефис
 * (`nT0Q8-XD`), так что `lastIndexOf('-')` резал бы его пополам. Обе попытки
 * были зелёными, потому что фикстуры повторяли догадку, а не сборку.
 *
 * Форма текста зависит от сборщика, `base`, минификатора, алфавита хеша и
 * относительного пути — надёжного разбора здесь быть не может. Поэтому ключ
 * приходит СТРОКОЙ из места вызова (`'page:Login'`, `'chunk:stripe-api'`), а
 * текст импортёра не читается вовсе. Параметр обязательный: вызов без ключа не
 * собирается. Полнота и уникальность ключей — под сторожем-обходом
 * `__tests__/lazyWithRetry.callKeys.guard.test.ts`.
 *
 * В ключе не должно быть запятой: оба носителя защёлки хранят СПИСОК ключей
 * через запятую (это тоже под сторожем).
 */

type Latch = { readonly keys: string[]; readonly legacy: boolean };

function parseLatch(raw: string | null): Latch {
  if (!raw) return { keys: [], legacy: false };
  if (LEGACY_MARK.has(raw)) return { keys: [], legacy: true };
  return { keys: raw.split(',').filter(Boolean), legacy: false };
}

/** Что уже перезагружалось: объединение обоих носителей. */
function readLatch(): Latch {
  const stored = parseLatch(safeStorageGet(RELOAD_FLAG));
  const fromUrl = parseLatch(readReloadQueryParam());
  return {
    keys: [...new Set([...stored.keys, ...fromUrl.keys])],
    legacy: stored.legacy || fromUrl.legacy,
  };
}

/** Значение параметра `chunk_retry` из адреса (или null). */
function readReloadQueryParam(): string | null {
  try {
    return new URLSearchParams(window.location.search).get(RELOAD_QUERY_PARAM);
  } catch {
    return null;
  }
}

/**
 * Best-effort: переписать метку в адресной строке БЕЗ перехода
 * (`history.replaceState`), чтобы она пережила перезагрузку там, где
 * sessionStorage недоступен. Пустой список — параметр удаляется целиком,
 * остальной query и hash не трогаются. Любой сбой здесь проглатывается: это
 * запасной носитель, основной — флаг хранилища.
 */
function writeReloadQueryParam(keys: string[]): void {
  try {
    const url = new URL(window.location.href);
    if (keys.length === 0) {
      if (!url.searchParams.has(RELOAD_QUERY_PARAM)) return;
      url.searchParams.delete(RELOAD_QUERY_PARAM);
    } else {
      const next = keys.join(',');
      if (url.searchParams.get(RELOAD_QUERY_PARAM) === next) return;
      url.searchParams.set(RELOAD_QUERY_PARAM, next);
    }
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // Best-effort only — см. док выше.
  }
}

/**
 * Resolves a lazy import, recovering from stale-deploy failures: when a
 * content-hashed chunk 404s because a new build replaced it, reload the page
 * once to fetch a fresh index.html. The sessionStorage flag prevents an
 * infinite reload loop if the failure is a genuine outage.
 */
export async function retryImport<T>(
  importer: Importer<T>,
  key: string,
): Promise<ModuleDefault<T>> {
  try {
    const module = await importer();
    // Успех снимает защёлку СВОЕГО файла — и из хранилища, и из адреса.
    // Старая метка «за всё» снимается целиком: ключа она не несёт, а
    // удавшийся импорт и есть доказательство, что выкат пережит.
    const latch = readLatch();
    if (latch.legacy) {
      safeStorageRemove(RELOAD_FLAG);
      writeReloadQueryParam([]);
    } else if (latch.keys.includes(key)) {
      const rest = latch.keys.filter((known) => known !== key);
      if (rest.length === 0) safeStorageRemove(RELOAD_FLAG);
      else safeStorageSet(RELOAD_FLAG, rest.join(','));
      writeReloadQueryParam(rest);
    }
    return module;
  } catch (error) {
    const latch = readLatch();
    // Свой файл уже перезагружался (или стоит старая метка «за всё»), либо
    // перезагрузок за визит уже столько, что это не выкат, а аутаж: дальше
    // честный отказ — его покажет граница падения, а не карусель.
    if (
      latch.legacy ||
      latch.keys.includes(key) ||
      latch.keys.length >= MAX_RELOADS_PER_VISIT
    ) {
      throw error;
    }
    const next = [...latch.keys, key];
    safeStorageSet(RELOAD_FLAG, next.join(','));
    writeReloadQueryParam(next);
    window.location.reload();
    // Keep the Suspense fallback visible while the page reloads.
    return new Promise<ModuleDefault<T>>(() => {});
  }
}

/**
 * `key` — ЯВНЫЙ ключ защёлки, ОБЯЗАТЕЛЬНЫЙ (см. большой докблок выше): он не
 * зависит ни от хеша в имени файла, ни от формы текста, который пишет сборщик.
 * Один модуль — один ключ, даже если вызовов несколько (страница пейволла
 * ходит через ЧЕТЫРЕ места вызова под одним `page:public-paywall`: роутер,
 * два в `PublicHostRouter.tsx` (ленивая страница и грелка) и визард квиза).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: Importer<T>,
  key: string,
): LazyExoticComponent<T> {
  return lazy(() => retryImport(importer, key));
}
